import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, fmtDate } from "../../api";
import { SectionTitle, StatusPill, EmptyState, Avatar } from "../../components/Atoms";
import { AIAuditButton, useAIAudit } from "../../components/AIAuditPanel";
import { InstagramInsightsButton, useInstagramInsights } from "../../components/InstagramInsightsPanel";
import {
  ArrowLeft, MapPin, EnvelopeSimple, Phone, InstagramLogo, YoutubeLogo, LinkedinLogo,
  ChartLineUp, Stack, GraduationCap, Megaphone, Link as LinkIcon, FileArrowDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientAmbassadorDetailPage() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const projectId = sp.get("project") || null;        // optional context: /client/ambassadors/:aid?project=PID
  const [amb, setAmb] = useState(null);
  const [subs, setSubs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const { openAudit, AuditModal } = useAIAudit();
  const { openInsights, InsightsModal } = useInstagramInsights();

  useEffect(() => {
    (async () => {
      try {
        const [a, app, sub, p] = await Promise.all([
          api.get(`/ambassadors/${aid}`),
          api.get(`/applications`),
          api.get(`/submissions`),
          api.get(`/projects`),
        ]);
        setAmb(a.data);
        const myProjIds = new Set((p.data || []).map(x => x.id));
        setProjects((app.data || []).filter(x => x.ambassador_id === aid && myProjIds.has(x.project_id)));
        setSubs((sub.data || []).filter(x => x.ambassador_id === aid && myProjIds.has(x.project_id)));
      } catch (e) {
        let msg;
        if (e?.response?.status === 403) {
          msg = "This creator is not approved on any of your projects.";
        } else if (e?.response?.status === 404) {
          msg = "Creator not found.";
        } else {
          msg = "Could not load creator profile.";
        }
        setErr(msg);
        toast.error(msg);
      } finally { setLoading(false); }
    })();
  }, [aid]);

  if (loading) return <div className="skeleton h-64" />;
  if (err || !amb) return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="btn-ghost flex items-center gap-1.5 text-sm" data-testid="back-from-client-amb">
        <ArrowLeft size={14} /> Back
      </button>
      <EmptyState title={err || "Creator not found"} />
    </div>
  );

  const skills = amb.skills || [];

  // Time-with-brand — earliest application date across this brand's projects
  const earliest = (projects || []).reduce((acc, p) => {
    const t = p.applied_at || p.created_at || "";
    if (!t) return acc;
    return !acc || t < acc ? t : acc;
  }, "");
  const tenureLabel = (() => {
    if (!earliest) return "—";
    const m = Math.max(0, Math.floor((Date.now() - new Date(earliest).getTime()) / (1000 * 60 * 60 * 24 * 30.44)));
    if (m === 0) return "This month";
    if (m < 12) return `${m} month${m === 1 ? "" : "s"}`;
    return `${Math.floor(m / 12)} yr${m >= 24 ? "s" : ""}${m % 12 ? ` ${m % 12}m` : ""}`;
  })();
  const tenure = { date: earliest, label: tenureLabel };

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="btn-ghost flex items-center gap-1.5 text-sm" data-testid="back-from-client-amb">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex justify-end gap-2">
        <InstagramInsightsButton size="lg" onClick={openInsights} testIdSuffix="client-detail" />
        <AIAuditButton
          ambassadorId={aid}
          projectId={projectId}
          size="lg"
          onClick={() => openAudit(aid, projectId)}
          label={projectId ? "AI Audit · this project" : "AI Audit"}
          testIdSuffix="client-detail"
        />
      </div>

      {/* Header */}
      <div className="glass p-5">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <Avatar src={amb.photo} name={amb.name} size={72} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-white text-2xl font-bold truncate">{amb.name}</div>
              <StatusPill status={amb.status} />
            </div>
            <div className="text-sm text-muted flex items-center gap-3 flex-wrap mt-1">
              {amb.city && <span className="flex items-center gap-1.5"><MapPin size={14} /> {amb.city}</span>}
              {amb.college && <span className="flex items-center gap-1.5"><GraduationCap size={14} /> {amb.college}</span>}
              {amb.course && <span data-testid="client-amb-course">· {amb.course}</span>}
              {amb.profession && <span data-testid="client-amb-profession">· {amb.profession}</span>}
              {amb.gender && <span data-testid="client-amb-gender" className="flex items-center gap-1.5">· {amb.gender}</span>}
              {amb.age && <span data-testid="client-amb-age">· {amb.age} yrs</span>}
            </div>
            <div className="text-sm text-muted flex items-center gap-3 flex-wrap mt-2">
              {amb.email && <span className="flex items-center gap-1.5" aria-label="Email"><EnvelopeSimple size={14} /> <span className="sr-only">Email:</span>{amb.email}</span>}
              {amb.phone && <span className="flex items-center gap-1.5" aria-label="Phone"><Phone size={14} /> <span className="sr-only">Phone:</span>{amb.phone}</span>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {amb.instagram && <a href={`https://instagram.com/${(amb.instagram || "").replace("@","")}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs text-[#EC4899] flex items-center gap-1.5" data-testid="client-amb-instagram"><InstagramLogo size={14} weight="bold" /> {amb.instagram}</a>}
              {amb.youtube && <a href={amb.youtube} target="_blank" rel="noreferrer" className="btn-ghost text-xs text-[#EC4899] flex items-center gap-1.5"><YoutubeLogo size={14} weight="bold" /> YouTube</a>}
              {amb.linkedin && <a href={amb.linkedin} target="_blank" rel="noreferrer" className="btn-ghost text-xs text-[#22D3EE] flex items-center gap-1.5"><LinkedinLogo size={14} weight="bold" /> LinkedIn</a>}
            </div>
          </div>
        </div>
        {amb.bio && <div className="text-sm text-muted mt-4 leading-relaxed">{amb.bio}</div>}
        {skills.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {skills.map(s => <span key={s} className="text-[10px] uppercase tracking-wider font-bold text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/30 px-2 py-0.5 rounded-full">{s}</span>)}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="glass p-4"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><InstagramLogo size={12} weight="bold" /> Followers</div><div className="text-white text-lg font-bold mt-1">{(amb.instagram_followers || 0).toLocaleString()}</div></div>
        <div className="glass p-4"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><ChartLineUp size={12} weight="bold" /> Performance</div><div className="text-white text-lg font-bold mt-1">{amb.performance_score || 0}</div></div>
        <div className="glass p-4"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><Stack size={12} weight="bold" /> Your projects</div><div className="text-white text-lg font-bold mt-1">{projects.filter(p => p.status === "Approved").length}</div></div>
        <div className="glass p-4"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><Megaphone size={12} weight="bold" /> Submissions</div><div className="text-white text-lg font-bold mt-1">{subs.length}</div></div>
        <div className="glass p-4" data-testid="client-amb-tenure">
          <div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><ChartLineUp size={12} weight="bold" /> With Brand</div>
          <div className="text-white text-lg font-bold mt-1">{tenure.label}</div>
          {tenure.date && <div className="text-[10px] text-muted mt-0.5">Since {fmtDate(tenure.date)}</div>}
        </div>
      </div>

      {/* On your projects */}
      <SectionTitle title="On your projects" subtitle="Campaigns this creator is part of with your brand" />
      {projects.length === 0 ? <EmptyState title="Not on any of your projects." /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {projects.map(a => (
            <Link to={`/client/projects/${a.project_id}`} key={a.id} className="glass p-4 hover-lift block" data-testid={`client-amb-proj-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-white font-bold truncate">{a.project_title}</div>
                <StatusPill status={a.status} />
              </div>
              <div className="text-[11px] text-muted mt-1">Applied {fmtDate(a.applied_at || a.created_at)}</div>
            </Link>
          ))}
        </div>
      )}

      {/* Submissions on your projects */}
      <SectionTitle title={`Submissions (${subs.length})`} subtitle="Task proofs on your campaigns" />
      {subs.length === 0 ? <EmptyState title="No submissions yet." /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {subs.map(s => (
            <div key={s.id} className="glass p-4" data-testid={`client-amb-sub-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-white font-bold truncate">{s.task_title}</div>
                  <Link to={`/client/projects/${s.project_id}`} className="text-xs text-muted hover:text-[#22D3EE] truncate block">{s.project_title}</Link>
                </div>
                <StatusPill status={s.status} />
              </div>
              {s.notes && <div className="text-sm text-muted mt-2">{s.notes}</div>}
              <div className="flex flex-wrap gap-2 mt-2">
                {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1.5"><LinkIcon size={14} /> Open link</a>}
                {s.file_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${s.file_url}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1.5"><FileArrowDown size={14} /> File</a>}
              </div>
              {s.feedback && <div className="text-xs text-[#F59E0B] mt-2">Feedback: {s.feedback}</div>}
            </div>
          ))}
        </div>
      )}
      {AuditModal}
      {InsightsModal}
    </div>
  );
}
